import { useState } from 'react';
import { ErrorState, LoadingState, Pager, Stars } from '../../components';
import { formatDate } from '../../lib/format/dates';
import type { ReviewedAs } from './api';
import { SectionLabel } from './people';
import { useProfileReviews } from './queries';
import { useDetailLinks } from '../orders/DetailDialogs';

const TITLE: Record<ReviewedAs, string> = { chef: 'Eater feedback', eater: 'Chef feedback' };
const PAGE_SIZE = 10;

/**
 * Feedback a profile has received — the bottom of the Profile tab in both the
 * Chef dialog (`as="chef"`: eaters rating the chef) and the Eater dialog
 * (`as="eater"`: chefs rating the eater). Same component, same endpoint.
 */
export function ReviewsReceived({ profileId, as }: { profileId: string; as: ReviewedAs }) {
  const [page, setPage] = useState(1);
  const reviews = useProfileReviews(profileId, as, page, PAGE_SIZE);
  const { openOrder } = useDetailLinks();

  return (
    <div className="mt-[18px]">
      <SectionLabel>{TITLE[as]}</SectionLabel>
      {reviews.isPending ? (
        <LoadingState label="Loading feedback…" />
      ) : reviews.isError ? (
        <ErrorState error={reviews.error} onRetry={() => void reviews.refetch()} compact />
      ) : reviews.data.items.length === 0 ? (
        <div className="text-[13px] text-muted">No feedback on file.</div>
      ) : (
        <>
          {reviews.data.items.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-2.5 border-t border-[rgba(143,68,32,.08)] py-[7px] text-[13px]"
            >
              <Stars n={r.rating} />
              <span className="min-w-0 flex-1 truncate text-[rgba(42,22,9,.72)]">
                {r.note || r.dishTitle || 'No comment.'}
              </span>
              {r.orderNumber && (
                <button
                  type="button"
                  onClick={() => openOrder(r.orderId)}
                  className="shrink-0 border-0 bg-transparent p-0 font-mono text-xs text-muted underline decoration-dotted underline-offset-2 hover:text-orange"
                >
                  {r.orderNumber}
                </button>
              )}
              <span className="shrink-0 truncate text-xs text-muted">{r.reviewerDisplayName}</span>
              <span className="shrink-0 text-xs text-muted">{formatDate(r.submittedAt)}</span>
            </div>
          ))}
          {reviews.data.total > PAGE_SIZE && (
            <Pager page={page} pageSize={PAGE_SIZE} total={reviews.data.total} onPageChange={setPage} noun="reviews" />
          )}
        </>
      )}
    </div>
  );
}
